import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { useMemo } from 'react';

import { cn } from '@/lib/utils';
import {
  detectCriteria,
  SCOUT_CRITERIA,
  type ScoutCriterionId,
} from '@/modules/scout-sourcing/detectCriteria';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';

interface ScoutPromptInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  className?: string;
  textareaClassName?: string;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  rows?: number;
  submitLabel?: string;
}

export function ScoutPromptInput({
  value,
  onChange,
  onSubmit,
  className,
  textareaClassName,
  disabled,
  loading,
  placeholder = 'e.g. Frontend engineer in Prague with React experience, 3+ years, fintech',
  rows = 2,
  submitLabel = 'Continue',
}: ScoutPromptInputProps) {
  const detected = useMemo(() => detectCriteria(value), [value]);
  const canSubmit = value.trim().length > 0 && !disabled && !loading;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      if (canSubmit) {
        onSubmit();
      }
    }
  };

  return (
    <div
      className={cn(
        'border-primary bg-card focus-within:border-primary rounded-2xl border-2 p-5 shadow-sm transition-colors',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || loading}
          rows={rows}
          placeholder={placeholder}
          aria-label="Describe the role"
          className={cn(
            'text-foreground placeholder:text-neutral-650 min-h-16 flex-1 resize-none border-none bg-transparent text-base leading-relaxed outline-none focus-visible:ring-0 disabled:opacity-60',
            textareaClassName,
          )}
        />
        <Button
          variant="primary"
          size="sm"
          aria-label="Continue"
          onClick={onSubmit}
          disabled={!canSubmit}
          loading={loading}
          className={cn(
            'shrink-0 gap-1 rounded-full px-3',
            !canSubmit && 'opacity-40',
          )}
        >
          {!loading && (
            <>
              <span className="text-sm">{submitLabel}</span>
              <ArrowRightIcon className="size-4" />
            </>
          )}
        </Button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2.5 border-t pt-4">
        {SCOUT_CRITERIA.map((criterion) => (
          <CriterionPill
            key={criterion.id}
            id={criterion.id}
            label={criterion.label}
            hint={criterion.hint}
            detected={detected.has(criterion.id)}
          />
        ))}
      </div>
    </div>
  );
}

interface CriterionPillProps {
  id: ScoutCriterionId;
  label: string;
  hint: string;
  detected: boolean;
}

function CriterionPill({ label, hint, detected }: CriterionPillProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex cursor-default items-center gap-2 text-sm font-medium transition-colors',
            detected ? 'text-secondary-500' : 'text-neutral-650',
          )}
        >
          <span
            className={cn(
              'flex size-4 items-center justify-center rounded-full border transition-colors',
              detected
                ? 'border-secondary-500 bg-secondary-500 text-white'
                : 'border-neutral-650/50 bg-transparent',
            )}
          >
            {detected && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 22 }}
              >
                <CheckIcon className="size-3" strokeWidth={3} />
              </motion.span>
            )}
          </span>
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}
